import {
  buildOptionalParams,
  buildPath,
  buildTemplatePath,
  CrudMode,
  Dialog,
  handleClose,
  Page,
  PageRoute,
  ScreenType,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { College, CollegeEditResource, CollegeViewResource, Permission } from "generated/openApi";
import "moment-timezone";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { CollegePageBody } from "./CollegePageBody";
import { CollegePageHeader } from "./CollegePageHeader";

const routeName = "college";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
  screenType: ScreenType = "standard";
}

class OptionalParams {
  closeOnSave?: boolean = false;
}

export const collegePageRoute: PageRoute<Params, OptionalParams> = {
  handle: "College",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
  getOptionalParams: (params) => buildOptionalParams(params),
};

export type CollegeContextValue = {
  onCreate?: (id: number) => void;
};

export const CollegeContext = createContext<CollegeContextValue>({} as CollegeContextValue);

export const CollegePage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const navigate = useNavigate();
  const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
  const [college, setCollege] = useState<College>({} as College);
  const [initCollege, setInitCollege] = useState<College>(college);
  const [viewResource, setViewResource] = useState<CollegeViewResource>({});
  const [editResource, setEditResource] = useState<CollegeEditResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [college] });
  const { validationErrors, setValidationErrors } = useValidationErrors();
  const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

  useEffect(() => {
    fetch(params.id);

    ApiService.colleges.getViewResource().then((response) => {
      setViewResource(response);
    });
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") {
      ApiService.colleges.getEditResource().then((response) => {
        setEditResource(response);
      });
    }
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${collegePageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

  const fetch = (id: number) => {
    ApiService.colleges.get(id).then((response) => {
      setCollege(response);
      setInitCollege(response);
      resetUnsavedChanges([response]);
    });
  };

  const pageHeader = (
    <CollegePageHeader
      rootPermission={ROOT_PERMISSION}
      handle={collegePageRoute.handle}
      crudMode={params.crudMode}
      reroute={reroute}
      fetch={fetch}
      college={college}
      setCollege={setCollege}
      initCollege={initCollege}
      setValidationErrors={setValidationErrors}
      unsavedChanges={unsavedChanges}
      resetUnsavedChanges={resetUnsavedChanges}
      closeOnSave={closeOnSave}
    />
  );

  const pageBody = (
    <CollegePageBody
      college={college}
      setCollege={setCollege}
      viewResource={viewResource}
      editResource={editResource}
      crudMode={params.crudMode}
      validationErrors={validationErrors}
    />
  );

  return (
    <Dialog
      isForm
      open={true}
      onClose={() => handleClose(navigate, unsavedChanges)}
      maxWidth="md"
      header={pageHeader}
      content={pageBody}
    />
  );
};
